/* booking-widget.js — K&S Booking System storefront widget.
 *
 * Dependency-free Web Component. Flow (TIME first, then group size, so the
 * customer sees availability before committing to a headcount):
 *   1 Location → 2 Date → 3 Time → 4 Students → 5 Questions (optional) → 6 Confirm
 *
 * Each start time carries maxStudents — the largest group that still fits from
 * it before the window closes / the next booking. On the Students step, groups
 * larger than that are disabled with a message telling the customer to pick an
 * earlier time or a smaller group.
 *
 * Confirm: POST /apps/booking/book places a 15-minute hold, then the matching
 * Students variant is added to the cart with the booking properties and the
 * customer continues through normal Shopify checkout. No personal details are
 * collected here — they come from the order.
 *
 * Failure modes: a 409/422 (slot just taken / no longer fits) refreshes
 * availability and returns to the date step with a notice. Network errors keep
 * the current step with a retry notice. The page is never broken.
 */
(function () {
  "use strict";

  var STEPS = ["location", "date", "time", "students", "questions", "confirm"];

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function money(cents) {
    return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(cents / 100);
  }

  function monthLabel(ym) {
    var parts = ym.split("-");
    var d = new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, 1));
    return d.toLocaleDateString("en-AU", { month: "long", year: "numeric", timeZone: "UTC" });
  }

  function addMonths(ym, delta) {
    var parts = ym.split("-");
    var y = Number(parts[0]);
    var m = Number(parts[1]) - 1 + delta;
    y += Math.floor(m / 12);
    m = ((m % 12) + 12) % 12;
    return y + "-" + String(m + 1).padStart(2, "0");
  }

  function currentMonthSydney() {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Australia/Sydney",
      year: "numeric",
      month: "2-digit",
    })
      .format(new Date())
      .slice(0, 7);
  }

  function dayLabel(iso) {
    var parts = iso.split("-");
    var d = new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])));
    return d.toLocaleDateString("en-AU", {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    });
  }

  // Clock arithmetic on a local 'HH:MM'. A lesson never crosses midnight given
  // store hours, so this is only used for display of the end time.
  function addMinutesHHMM(hhmm, minutes) {
    var parts = hhmm.split(":");
    var total = Number(parts[0]) * 60 + Number(parts[1]) + minutes;
    var h = Math.floor(total / 60) % 24;
    var m = total % 60;
    return String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");
  }

  function plural(n, word) {
    return n + " " + word + (n === 1 ? "" : "s");
  }

  class KnsBookingWidget extends HTMLElement {
    connectedCallback() {
      this.proxyUrl = this.dataset.proxyUrl || "/apps/booking";
      this.serviceId = this.dataset.service || "sharpening";
      try {
        this.variants = JSON.parse(this.dataset.variants || "[]");
        this.locations = JSON.parse(this.dataset.locations || "[]");
      } catch (e) {
        this.variants = [];
        this.locations = [];
      }
      this.variants.sort(function (a, b) {
        return a.students - b.students;
      });

      this.state = {
        step: "location",
        students: null,
        locationId: null,
        month: currentMonthSydney(),
        availability: null, // last /availability payload
        selectedDate: null,
        selectedStart: null, // {sessionId, startAtUtc, time, maxStudents}
        answers: {},
        notice: null,
        busy: false,
      };

      this.body = this.querySelector("[data-body]");
      this.progress = this.querySelector("[data-progress]");
      var boot = this.querySelector("[data-boot]");
      if (boot) boot.remove();

      if (this.variants.length === 0 || this.locations.length === 0) {
        this.body.appendChild(
          el(
            "p",
            "kns-bkg__error",
            "Booking is not configured yet. Please contact the store to book your class.",
          ),
        );
        return;
      }
      this.render();
    }

    setState(patch) {
      Object.assign(this.state, patch);
      this.render();
    }

    // ------------------------------------------------------------------ data

    // Price/duration for a group size, from the availability pricing table.
    pricingFor(students) {
      var list = (this.state.availability && this.state.availability.pricing) || [];
      for (var i = 0; i < list.length; i++) if (list[i].students === students) return list[i];
      return null;
    }

    async fetchAvailability() {
      var s = this.state;
      var url =
        this.proxyUrl +
        "/availability?service=" +
        encodeURIComponent(this.serviceId) +
        "&location=" +
        encodeURIComponent(s.locationId) +
        "&month=" +
        encodeURIComponent(s.month);
      var res = await fetch(url, { credentials: "omit", headers: { accept: "application/json" } });
      if (!res.ok) throw new Error("HTTP " + res.status);
      return res.json();
    }

    async loadMonth(month) {
      this.setState({ busy: true, notice: null, month: month });
      try {
        var data = await this.fetchAvailability();
        this.setState({ availability: data, busy: false });
      } catch (e) {
        console.warn("[kns-booking] availability failed", e);
        this.setState({
          busy: false,
          notice: "Could not load available times — please try again.",
        });
      }
    }

    async submitBooking() {
      var s = this.state;
      var questions = (s.availability && s.availability.questions) || [];
      var answers = [];
      for (var i = 0; i < questions.length; i++) {
        var q = questions[i];
        var value = (s.answers[q.key] || "").trim();
        if (value) answers.push({ key: q.key, label: q.label, value: value });
      }

      this.setState({ busy: true, notice: null });
      try {
        var res = await fetch(this.proxyUrl + "/book", {
          method: "POST",
          credentials: "omit",
          headers: { "content-type": "application/json", accept: "application/json" },
          body: JSON.stringify({
            service: this.serviceId,
            sessionId: s.selectedStart.sessionId,
            startAtUtc: s.selectedStart.startAtUtc,
            students: s.students,
            answers: answers,
          }),
        });
        var data = await res.json();
        if (res.status === 409 || res.status === 422 || res.status === 404) {
          // Slot gone / no longer fits — refresh and send them back to dates.
          this.setState({
            step: "date",
            selectedDate: null,
            selectedStart: null,
            busy: false,
            notice:
              (data && data.message) || "That time was just taken. Please pick another slot.",
          });
          this.loadMonth(s.month);
          return;
        }
        if (!res.ok) throw new Error("HTTP " + res.status);

        var cartRes = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json" },
          body: JSON.stringify({
            items: [{ id: Number(data.variantId), quantity: 1, properties: data.properties }],
          }),
        });
        if (!cartRes.ok) throw new Error("cart HTTP " + cartRes.status);
        window.location.href = "/cart";
      } catch (e) {
        console.warn("[kns-booking] booking failed", e);
        this.setState({
          busy: false,
          notice: "Something went wrong placing your booking. Please try again.",
        });
      }
    }

    // ---------------------------------------------------------------- render

    render() {
      var s = this.state;
      this.progress.hidden = false;
      this.progress.textContent = "Step " + (STEPS.indexOf(s.step) + 1) + " of " + STEPS.length;
      this.body.textContent = "";

      if (s.notice) {
        this.body.appendChild(el("p", "kns-bkg__notice", s.notice));
      }

      switch (s.step) {
        case "location":
          return this.renderLocation();
        case "date":
          return this.renderDate();
        case "time":
          return this.renderTime();
        case "students":
          return this.renderStudents();
        case "questions":
          return this.renderQuestions();
        case "confirm":
          return this.renderConfirm();
      }
    }

    backLink(toStep) {
      var self = this;
      var a = el("button", "kns-bkg__back", "← Back");
      a.type = "button";
      a.addEventListener("click", function () {
        self.setState({ step: toStep, notice: null });
      });
      return a;
    }

    heading(text) {
      return el("p", "kns-bkg__step-title", text);
    }

    renderLocation() {
      var self = this;
      this.body.appendChild(this.heading("Choose your location"));
      var list = el("div", "kns-bkg__options");
      this.locations.forEach(function (loc) {
        var btn = el("button", "kns-bkg__option");
        btn.type = "button";
        if (self.state.locationId === loc.id) btn.classList.add("is-selected");
        btn.appendChild(el("span", "kns-bkg__option-label", loc.label));
        if (loc.address) btn.appendChild(el("span", "kns-bkg__option-sub", loc.address));
        btn.addEventListener("click", function () {
          self.state.locationId = loc.id;
          self.state.step = "date";
          self.state.selectedDate = null;
          self.state.selectedStart = null;
          self.state.students = null;
          self.loadMonth(currentMonthSydney());
        });
        list.appendChild(btn);
      });
      this.body.appendChild(list);
    }

    renderDate() {
      var self = this;
      var s = this.state;
      this.body.appendChild(this.heading("Pick a date"));

      var nav = el("div", "kns-bkg__monthnav");
      var prev = el("button", "kns-bkg__monthbtn", "‹");
      prev.type = "button";
      prev.disabled = s.month <= currentMonthSydney();
      prev.addEventListener("click", function () {
        self.loadMonth(addMonths(s.month, -1));
      });
      var next = el("button", "kns-bkg__monthbtn", "›");
      next.type = "button";
      next.addEventListener("click", function () {
        self.loadMonth(addMonths(s.month, 1));
      });
      nav.appendChild(prev);
      nav.appendChild(el("span", "kns-bkg__month", monthLabel(s.month)));
      nav.appendChild(next);
      this.body.appendChild(nav);

      if (s.busy) {
        this.body.appendChild(el("p", "kns-bkg__loading", "Loading available dates…"));
      } else if (s.availability) {
        var days = s.availability.days || [];
        if (days.length === 0) {
          this.body.appendChild(
            el("p", "kns-bkg__empty", "No slots available this month at this location. Try another month."),
          );
        } else {
          var list = el("div", "kns-bkg__days");
          days.forEach(function (day) {
            var count = (day.starts || []).length;
            var btn = el("button", "kns-bkg__day");
            btn.type = "button";
            btn.appendChild(el("span", "kns-bkg__day-date", dayLabel(day.date)));
            btn.appendChild(el("span", "kns-bkg__day-count", plural(count, "slot")));
            btn.addEventListener("click", function () {
              self.setState({ selectedDate: day.date, step: "time", notice: null });
            });
            list.appendChild(btn);
          });
          this.body.appendChild(list);
        }
      }
      this.body.appendChild(this.backLink("location"));
    }

    renderTime() {
      var self = this;
      var s = this.state;
      this.body.appendChild(this.heading("Pick a time — " + dayLabel(s.selectedDate)));
      var day = ((s.availability && s.availability.days) || []).find(function (d) {
        return d.date === s.selectedDate;
      });
      var starts = day ? day.starts || [] : [];
      var list = el("div", "kns-bkg__times");
      starts.forEach(function (start) {
        var btn = el("button", "kns-bkg__time");
        btn.type = "button";
        btn.appendChild(el("span", "kns-bkg__time-start", start.time));
        btn.appendChild(el("span", "kns-bkg__time-max", "up to " + plural(start.maxStudents, "student")));
        btn.addEventListener("click", function () {
          // Keep a previously-chosen group size only if it still fits this start.
          var keep = s.students && s.students <= start.maxStudents ? s.students : null;
          self.setState({ selectedStart: start, students: keep, step: "students", notice: null });
        });
        list.appendChild(btn);
      });
      this.body.appendChild(list);
      this.body.appendChild(this.backLink("date"));
    }

    renderStudents() {
      var self = this;
      var s = this.state;
      var start = s.selectedStart;
      var max = start ? start.maxStudents : 0;

      this.body.appendChild(this.heading("How many students? — " + dayLabel(s.selectedDate) + ", " + start.time));

      var list = el("div", "kns-bkg__options");
      this.variants.forEach(function (v) {
        var fits = v.students <= max;
        var price = self.pricingFor(v.students);
        var btn = el("button", "kns-bkg__option");
        btn.type = "button";
        if (s.students === v.students) btn.classList.add("is-selected");
        if (!fits) {
          btn.classList.add("is-disabled");
          btn.disabled = true;
        }
        btn.appendChild(el("span", "kns-bkg__option-label", v.label));
        btn.appendChild(el("span", "kns-bkg__option-price", money(v.priceCents)));
        if (!fits) {
          btn.appendChild(el("span", "kns-bkg__option-sub", "Needs a longer / earlier slot"));
        } else if (price) {
          btn.appendChild(
            el("span", "kns-bkg__option-sub", "Ends " + addMinutesHHMM(start.time, price.durationMinutes)),
          );
        }
        if (fits) {
          btn.addEventListener("click", function () {
            var hasQuestions = ((s.availability && s.availability.questions) || []).length > 0;
            self.setState({ students: v.students, step: hasQuestions ? "questions" : "confirm", notice: null });
          });
        }
        list.appendChild(btn);
      });
      this.body.appendChild(list);

      // The message the owner asked for: when the picked time can't hold every
      // group size, say so and point the customer at the fix.
      var biggest = this.variants.length ? this.variants[this.variants.length - 1].students : 0;
      if (max < biggest) {
        this.body.appendChild(
          el(
            "p",
            "kns-bkg__hint",
            "The " + start.time + " start fits up to " + plural(max, "student") +
              ". For a larger group, go back and pick an earlier time.",
          ),
        );
      } else {
        this.body.appendChild(
          el("p", "kns-bkg__hint", "Each additional student adds half an hour to the lesson."),
        );
      }
      this.body.appendChild(this.backLink("time"));
    }

    renderQuestions() {
      var self = this;
      var s = this.state;
      this.body.appendChild(this.heading("A couple of quick questions (optional)"));
      var form = el("div", "kns-bkg__questions");
      ((s.availability && s.availability.questions) || []).forEach(function (q) {
        var wrap = el("div", "kns-bkg__question");
        wrap.appendChild(el("div", "kns-bkg__question-label", q.label || q.key || ""));
        var input = document.createElement("textarea");
        input.className = "kns-bkg__question-input";
        input.rows = 2;
        input.value = s.answers[q.key] || "";
        input.addEventListener("input", function () {
          s.answers[q.key] = input.value;
        });
        wrap.appendChild(input);
        form.appendChild(wrap);
      });
      this.body.appendChild(form);

      var row = el("div", "kns-bkg__actions");
      var cont = el("button", "kns-bkg__primary", "Continue");
      cont.type = "button";
      cont.addEventListener("click", function () {
        self.setState({ step: "confirm", notice: null });
      });
      row.appendChild(cont);
      this.body.appendChild(row);
      this.body.appendChild(this.backLink("students"));
    }

    renderConfirm() {
      var self = this;
      var s = this.state;
      var variant = this.variants.find(function (v) {
        return v.students === s.students;
      });
      var location = this.locations.find(function (l) {
        return l.id === s.locationId;
      });
      var price = this.pricingFor(s.students);
      var endTime = price ? addMinutesHHMM(s.selectedStart.time, price.durationMinutes) : "";

      this.body.appendChild(this.heading("Review your booking"));
      var summary = el("dl", "kns-bkg__summary");
      [
        ["Class", variant ? variant.label : plural(s.students, "student")],
        ["Location", location ? location.label : s.locationId],
        ["Date", dayLabel(s.selectedDate)],
        ["Time", endTime ? s.selectedStart.time + " – " + endTime : s.selectedStart.time],
        ["Price", variant ? money(variant.priceCents) : ""],
      ].forEach(function (pair) {
        summary.appendChild(el("dt", "", pair[0]));
        summary.appendChild(el("dd", "", pair[1]));
      });
      this.body.appendChild(summary);

      var row = el("div", "kns-bkg__actions");
      var confirm = el(
        "button",
        "kns-bkg__primary",
        s.busy ? "Reserving your spot…" : "Confirm & add to cart",
      );
      confirm.type = "button";
      confirm.disabled = s.busy;
      confirm.addEventListener("click", function () {
        self.submitBooking();
      });
      row.appendChild(confirm);
      this.body.appendChild(row);
      this.body.appendChild(
        el(
          "p",
          "kns-bkg__hint",
          "Your spot is held for 15 minutes while you check out. Payment is taken at checkout.",
        ),
      );
      this.body.appendChild(this.backLink("students"));
    }
  }

  if (!customElements.get("kns-booking-widget")) {
    customElements.define("kns-booking-widget", KnsBookingWidget);
  }
})();
